import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { findWholeListShortfall, type WholeListPage } from '@/utils/whole-list'

export type WholeListResource = 'connections' | 'measurements' | 'networks' | 'nodes'

/**
 * ADR 0013's tripwire on a list fetched whole on one page: a page short of its own `total_count` is
 * elements missing from the drawing, not a paging bug, so it is said out loud rather than rendered
 * as a smaller network.
 */
export function useWholeListTruncationWarning(
	resource: WholeListResource,
	page: WholeListPage<unknown> | undefined,
): void {
	const { t } = useTranslation()
	const shortfall = page ? findWholeListShortfall(page) : null
	const received = shortfall?.received
	const totalCount = shortfall?.totalCount

	useEffect(() => {
		if (received === undefined || totalCount === undefined) {
			return
		}

		// Deferred a tick because `Toaster` is a sibling *after* the router outlet in `__root`:
		// on a first page load this effect runs before it subscribes, and sonner drops anything
		// published until then — the warning this hook exists to give would be the one lost.
		// The id keeps a re-render of the same short page updating one warning rather than stacking.
		const timer = setTimeout(() => {
			toast.warning(
				t('whole_list.truncated', {
					resource: t(`whole_list.resource.${resource}`),
					received,
					total: totalCount,
				}),
				{ id: `whole-list-truncated-${resource}` },
			)
		})

		return () => clearTimeout(timer)
	}, [received, resource, t, totalCount])
}
